import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TikTokGlyph from 'src/assets/tiktok-glyph.svg';
import { tTypeAttachment } from '../../MessageBubble.type';

// Vite's svgr plugin types SVG imports as `string` at the type level, but at
// runtime they are React components. This double cast mirrors the pattern in
// `AutomationSelection.component.tsx`.
const TikTokGlyphSvg = TikTokGlyph as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

/**
 * SHARE_POST renderer for TikTok shared video posts (INT-929).
 *
 * Rendered when `decryptedAttachment.mediaType === MediaType.SHARE_POST`.
 * The wire shape carries `video_id` (canonical, always present on success),
 * plus `title`, `author_name`, `author_url`, `thumbnail_url`, `embed_url`.
 * Each is the empty string when extraction permanently failed (U2 contract).
 *
 * Security invariants:
 * - `title` / `author_name` render as JSX text nodes ONLY. Never use
 *   `dangerouslySetInnerHTML`.
 * - CTA URL is built with `encodeURIComponent` on both `video_id` and
 *   `author_name` to neutralize injection attempts.
 *
 * Layout target:
 * - Fixed `w-72` (matches VideoContent).
 * - Thumbnail: `aspect-ratio: 9/16` (TikTok native), with `bg-base-200`
 *   loading placeholder and TikTok-glyph fallback on empty / onError.
 * - Title: `line-clamp-2 text-sm font-medium`.
 * - Author: plain `@{author_name}` — NOT linked. `text-xs line-clamp-1`.
 * - CTA: `<a target="_blank" rel="noopener noreferrer">` with `aria-label`.
 */
export default function SharePostContent(props: tTypeAttachment) {
  const { t } = useTranslation('chat/chat');
  const { attachment } = props;

  // U2 wire contract: each of these is the empty string on permanent
  // extraction failure. Treat undefined the same as empty for safety.
  const videoId = attachment.video_id ?? '';
  const title = attachment.title ?? '';
  const authorName = attachment.author_name ?? '';
  const rawThumbnailUrl = attachment.thumbnail_url ?? '';
  // Only trust https thumbnails. A non-https URL from a compromised upstream
  // would still be fetched by the browser (tracking beacon / SSRF probing);
  // fall back to the placeholder instead.
  const thumbnailUrl = rawThumbnailUrl.startsWith('https://')
    ? rawThumbnailUrl
    : '';

  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  useEffect(() => {
    setThumbnailFailed(false);
  }, [thumbnailUrl]);

  const showPlaceholder = thumbnailUrl === '' || thumbnailFailed;

  // CTA URL construction. When `author_name` is empty, TikTok still resolves
  // by video_id alone (verified during the U1 spike). When `video_id` itself
  // is empty we hide the CTA — there's nothing actionable to click through.
  const ctaUrl = (() => {
    if (videoId === '') return null;
    if (authorName === '') {
      return `https://www.tiktok.com/video/${encodeURIComponent(videoId)}`;
    }
    return `https://www.tiktok.com/@${encodeURIComponent(authorName)}/video/${encodeURIComponent(videoId)}`;
  })();

  const altText = title !== '' ? title : t('sharePostPlaceholder');

  // The CTA wraps the entire card on success. On missing video_id we render
  // the same visual tree without the anchor.
  const card = (
    <div
      className="w-72 mb-1 rounded-md overflow-hidden border border-base-200 bg-base-100 flex flex-col"
      data-testid="share-post-card"
    >
      <div
        className="w-full aspect-[9/16] bg-base-200 flex items-center justify-center"
        data-testid="share-post-thumbnail-container"
      >
        {showPlaceholder ? (
          <div
            className="flex flex-col items-center justify-center gap-2 text-base-content/60"
            data-testid="share-post-placeholder"
          >
            <TikTokGlyphSvg className="w-12 h-12" />
            <span className="text-xs">{t('sharePostPlaceholder')}</span>
          </div>
        ) : (
          <img
            src={thumbnailUrl}
            alt={altText}
            className="w-full h-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setThumbnailFailed(true)}
            data-testid="share-post-thumbnail"
          />
        )}
      </div>
      <div className="p-2 flex flex-col gap-1">
        {title !== '' && (
          <span
            className="line-clamp-2 text-sm font-medium"
            data-testid="share-post-title"
          >
            {title}
          </span>
        )}
        {authorName !== '' && (
          <span
            className="text-xs text-base-content/60 line-clamp-1"
            data-testid="share-post-author"
          >
            @{authorName}
          </span>
        )}
        {ctaUrl !== null && (
          <div
            className="flex items-center gap-1 text-xs text-primary mt-1"
            data-testid="share-post-cta-label"
          >
            <TikTokGlyphSvg className="w-3 h-3" />
            <span>{t('viewOnTikTok')}</span>
          </div>
        )}
      </div>
    </div>
  );

  if (ctaUrl === null) {
    return card;
  }

  return (
    <a
      href={ctaUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('viewOnTikTok')}
      className="block no-underline text-base-content hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary rounded-md"
      data-testid="share-post-cta"
    >
      {card}
    </a>
  );
}
